import React from "react";
import {
  Box,
  Stack,
  Card,
  Group,
  Text,
  Title,
  Center,
  ScrollArea,
} from "@mantine/core";
import useTree from "../../../../../hooks/corral/tree";
import { useCurrentUser } from "../../../../../hooks/currentUser";

const quadrantOrder = {
  source: 0,
  mind: 1,
  body: 2,
  spirit: 3,
  action: 4,
  assistance: 5,
  assurance: 6,
  name: 7,
};

const TreeCard = ({ treeId, variant, mode, onActive }) => {
  const { tree } = useTree({ treeId });
  const isActive =
    tree &&
    tree.terminusActive &&
    ((mode === "question" && tree.direction === "clockwise") ||
      (mode === "answer" && tree.direction === "counter-clockwise"));
  React.useEffect(() => {
    if (isActive && onActive) {
      onActive();
    }
  }, [isActive, onActive]);

  if (!isActive) return null;
  if (treeId === "source") return null;
  return (
    <Card withBorder radius="md" p="sm">
      <Group position="apart" mb="xs">
        <Text size="xs" c="dimmed" style={{ textTransform: "capitalize" }}>
          {tree.name}
        </Text>
      </Group>
      <Text size="sm" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {variant}
      </Text>
    </Card>
  );
};

const QASection = ({
  label,
  type,
  name,
  description,
  variants,
  mode,
  treeKeys,
  maxHeight,
  hasAny,
  setHasAny,
}) => (
  <Box style={{ display: hasAny ? "block" : "none" }}>
    <Center mt={mode === "answer" ? "xl" : 0}>
      <Title order={4} mb="sm">
        {label} {type} -- {name}
      </Title>
    </Center>
    <Text size="sm" mb="sm">
      {description}
    </Text>
    <ScrollArea style={{ maxHeight, overflowY: "auto" }}>
      <Stack spacing="sm">
        {treeKeys.map(
          (treeId) =>
            (variants[treeId] || treeId === "source") && (
              <TreeCard
                key={`${mode}-${treeId}`}
                treeId={treeId}
                variant={variants[treeId]}
                mode={mode}
                onActive={() => setHasAny(true)}
              />
            ),
        )}
      </Stack>
    </ScrollArea>
  </Box>
);

const QuestionsAnswers = ({ trees, maxHeight = 400 }) => {
  const { currentUser } = useCurrentUser();
  const { activeCharacter } = currentUser;
  const { question, answer } = activeCharacter.questionAnswer;

  const treeKeys = trees
    .map((tree) => tree.id)
    .sort((a, b) => (quadrantOrder[a] ?? 999) - (quadrantOrder[b] ?? 999));

  const [hasQuestion, setHasQuestion] = React.useState(false);
  const [hasAnswer, setHasAnswer] = React.useState(false);

  return (
    <Box my="sm" style={{ minWidth: "100%" }}>
      <QASection
        label="Question"
        type={question.type}
        name={question.name}
        description={question.description}
        variants={question.variants}
        mode="question"
        treeKeys={treeKeys}
        maxHeight={maxHeight}
        hasAny={hasQuestion}
        setHasAny={setHasQuestion}
      />
      <QASection
        label="Answer"
        type={answer.type}
        name={answer.name}
        description={answer.description}
        variants={answer.variants}
        mode="answer"
        treeKeys={treeKeys}
        maxHeight={maxHeight}
        hasAny={hasAnswer}
        setHasAny={setHasAnswer}
      />
    </Box>
  );
};

export default QuestionsAnswers;
